import { getLocalStorage } from '@shared/core';
import { deriveQueryStringHelpersFromSearchString } from '@shared/core/queryString';
import type { EventType } from '@graphql/generated';
import { getBrowserId, getSessionId, getInstanceId } from './getTrackerIds';
import { withWindow } from '@shared/utils/withWindow';

let clientWidth = withWindow(globalWindow => globalWindow.innerWidth ?? 0, 0);
let clientHeight = withWindow(globalWindow => globalWindow.innerHeight ?? 0, 0);

// Global resize listener for tracking viewport dimensions across all analytics events
// Intentionally not removed as it's needed for the lifetime of the application
let hasResizeListener = false;
const listenForResize = () => {
  withWindow(globalWindow => {
    if (!hasResizeListener) {
      hasResizeListener = true;
      // Resize listener is noisy and fires frequently - the current use is acceptable
      // but we should add debouncing if adding more logic here to avoid performance issues
      globalWindow.addEventListener('resize', () => {
        clientWidth = globalWindow.innerWidth;
        clientHeight = globalWindow.innerHeight;
      });
    }
  });
};
listenForResize();

/**
 * Get shared context properties that are included with all analytics events
 */
export const getSharedContextProperties = () => {
  const locationSearch = withWindow(globalWindow => globalWindow.location?.search, '');
  const { getValueString } = deriveQueryStringHelpersFromSearchString(locationSearch);

  return {
    ref: getValueString('ref'),
    browserId: getBrowserId(),
    sessionId: getSessionId(),
    instanceId: getInstanceId(),
    buildNumber: withWindow(globalWindow => globalWindow.buildNumber, undefined),
    clientWidth,
    clientHeight,
  };
};

/**
 * Get platform-specific properties from localStorage
 */
export const getPlatformProperties = () => {
  const localStorage = getLocalStorage();
  const joyPlatform = localStorage.getItem('segmentPlatform');
  const joyPlatformData = localStorage.getItem('segmentPlatformData');

  return {
    joyPlatform,
    joyPlatformData,
  };
};

/**
 * Get viewport size based on window width
 * Mobile: <= 1024px, Desktop: > 1024px
 */
export const getViewportSize = (): 'mobile' | 'desktop' => {
  return withWindow(globalWindow => (globalWindow.innerWidth <= 1024 ? 'mobile' : 'desktop'), 'desktop');
};

export type EventGroupContext = Readonly<{
  postgresEventId?: string;
  firebaseEventId?: string;
  joyEventType?: EventType;
}>;

/**
 * Merge event group context with shared context properties
 */
export const getMergedEventContext = (groupContext: EventGroupContext, customContext: Record<string, unknown> = {}): Record<string, unknown> => {
  return {
    postgresEventId: groupContext.postgresEventId,
    firebaseEventId: groupContext.firebaseEventId,
    joyEventType: groupContext.joyEventType,
    ...customContext,
    ...getSharedContextProperties(),
  };
};
